import { inject, Injectable } from '@angular/core';
import { getToken, Messaging, onMessage } from '@angular/fire/messaging';
import { GraphqlService } from './graphql.service';
import { environment } from '../../../environments/environment';
import { USER_UPDATE } from '../../graphql/mutations/user';
import { StorageService } from './storage.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DiscountNotificationComponent } from '../../shared/components/discount-notification/discount-notification.component';

@Injectable({
  providedIn: 'root',
})
export class MessagingService {
  private readonly _messaging = inject(Messaging);
  private modalService = inject(NgbModal);

  constructor(
    private graphql: GraphqlService,
    private storage: StorageService
  ) {}

  getFirebaseToken(userId: string) {
    console.log('Requesting Firebase token for user:', userId);
    this._onMessage();

    getToken(this._messaging, {
      vapidKey: environment.vapidKey,
    })
      .then((currentToken) => {
        if (!currentToken) {
          console.log('No registration token available. Request permission to generate one.');
          return;
        }
        this.setUserFCMToken(userId, currentToken);
      })
      .catch((err) => {
        console.error('An error occurred while retrieving token.', err);
      });
  }

  requestPermission(userId: string) {
    Notification.requestPermission().then((permission) => {
      if (permission === 'granted') {
        console.log('Notification permission granted.');
        this.getFirebaseToken(userId);
      } else {
        console.log('Unable to get permission to notify.');
      }
    });
  }

  async setUserFCMToken(userId: string, token: string) {
    await this.graphql.runMutation(
      USER_UPDATE,
      {
        updateUserId: userId,
        change: { fcm_token: token },
      },
      'API_SERVER'
    );
  }

  private _onMessage(): void {
    console.log('Setting up message listener');
    onMessage(this._messaging, {
      next: (payload) => {
        console.log('Message received:', payload);

        if (!payload.data) return;

        switch (payload.data['channel']) {
          case 'authorization':
            this.showAuthorizationNotification(payload);
            break;

          case 'test_notification':
            this.notify(payload);
            this.showAuthorizationNotification(payload);
            break;

          case 'discount_notification':
            this.showDiscountNotification(payload);
            break;
        }
      },
      error: (error) => console.error('Message error', error),
      complete: () => console.log('Done listening to messages'),
    });
  }

  notify(payload: any) {
    const title = payload.data?.title || 'Notification';
    const body = payload.data?.body || 'New notification received';

    new Notification(title, {
      body,
      silent: false,
    });

    this.playSound();
  }

  private showAuthorizationNotification(payload: any) {
    new Notification(payload.notification?.title || 'Authorized', {
      body: payload.notification?.body || 'Authorized',
      silent: false,
    });
    this.playSound();
  }

  private showDiscountNotification(payload: any) {
    const modalRef = this.modalService.open(DiscountNotificationComponent, {
      backdrop: 'static',
      centered: true,
    });

    modalRef.componentInstance.notificationTitle = payload.notification?.title || 'Discount';
    modalRef.componentInstance.notificationBody = payload.notification?.body || 'You have a new discount';

    this.playSound();

    modalRef.result
      .then(() => {
        console.log('User acknowledged the discount notification');
        // Optional: Call sync API here
        // this.syncAll();
      })
      .catch(() => {
        console.log('Discount modal dismissed');
      });
  }

  private playSound() {
    const audio = new Audio('https://d3qckg8fw71fwh.cloudfront.net/sounds/order.mp3');
    audio.play().catch((err) => console.warn('Sound playback failed:', err));
  }
}
